/* eslint-disable no-unused-vars */
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useToleranceDataManager} from './useToleranceDataManager'
import {useHousingValidator} from './useHousingValidator'
import type {ExpertHousing, FrontendError} from '~/types/oring-api-responses'

export function useOringUnitConverter() {
  const store = useORingCalculatorStore()
  const {oringCalculatorImperial} = storeToRefs(store)

  /**
   * Convert a value between metric (mm) and imperial (inch) units
   * @param toImperial - true to convert from mm to inch, false to convert from inch to mm
   * @param value - the value to convert
   * @param decimalPlaces - number of decimal places to round to
   * @returns the converted value
   */
  function convertLength(toImperial: boolean, value: number | null, decimalPlaces: number = 3): number | null {
    if (value === null || value === undefined) {
      return null
    }

    if (toImperial) {
      // mm to inch: divide by 25.4
      return roundNumber(value / 25.4, decimalPlaces)
    } else {
      // inch to mm: multiply by 25.4
      return roundNumber(value * 25.4, decimalPlaces)
    }
  }

  /**
   * Round a number to the specified decimal places
   */
  function roundNumber(number: number, decimalPlaces: number = 3): number {
    return parseFloat(number.toFixed(decimalPlaces))
  }

  /**
   * Convert all parameter input data to the new unit system
   * @param parameterInputData - current parameter data
   * @param toImperial - true to convert to imperial, false to convert to metric
   * @returns converted parameter data
   */
  function convertParameterInputData(
    parameterInputData: Record<string, number | null>,
    toImperial: boolean
  ): Record<string, number | null> {
    const convertedData: Record<string, number | null> = {}

    for (const [key, value] of Object.entries(parameterInputData)) {
      if (value !== null && value !== undefined) {
        // Use higher precision for imperial (4 decimal places) vs metric (3 decimal places)
        const decimalPlaces = toImperial ? 4 : 3
        convertedData[key] = convertLength(toImperial, value, decimalPlaces)
      } else {
        convertedData[key] = value
      }
    }

    return convertedData
  }

  /**
   * Perform unit switch for tolerance data - converts parameter values and triggers API calls
   * @param toleranceData - current tolerance selections
   * @param parameterInputData - current parameter values
   * @param emitToleranceData - function to emit updated tolerance data
   * @param emitParameterData - function to emit updated parameter data
   * @param emitValidationErrors - function to emit validation errors
   * @param emitHousingValidated - function to emit validated housing
   * @param emitApiError - function to emit API errors
   * @param imperial
   */
  async function performUnitSwitch(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>,
    emitToleranceData: (data: Record<string, string | null>) => void,
    emitParameterData: (data: Record<string, number | null>) => void,
    emitValidationErrors: (errors: FrontendError[]) => void,
    emitHousingValidated: (housing: ExpertHousing) => void,
    emitApiError: (error: any) => void,
    imperial: boolean|null = null
  ): Promise<void> {
    // Get the new imperial setting after toggle
    const newImperialSetting = imperial !== null ? imperial : oringCalculatorImperial.value

    // Convert all parameter input values to the new unit system
    const convertedParameterData = convertParameterInputData(parameterInputData, newImperialSetting)

    // Emit the converted parameter data
    emitParameterData(convertedParameterData)
  }

  return {
    convertLength,
    roundNumber,
    convertParameterInputData,
    performUnitSwitch
  }
}
